import { BrowserService } from '@awork/_shared/services/browser-service/browser.service'
import type { OnInit } from '@angular/core'
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild
} from '@angular/core'
import { trigger, style, transition, animate } from '@angular/animations'

import { DismissButtonComponent } from '../../icon-buttons/dismiss-button/dismiss-button.component'
import { Size } from '@awork/_shared/types/size'
import type { OverlayColor } from '@awork/_shared/types/color'
import { Color } from '@awork/_shared/types/color'

@Component({
  selector: 'aw-overlay',
  templateUrl: './overlay.component.html',
  styleUrls: ['./overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DismissButtonComponent],
  animations: [
    trigger('overlay', [
      transition('* => void', [style({ opacity: '1' }), animate('.2s ease-in', style({ opacity: '0' }))]),
      transition(
        'void => *',
        [style({ opacity: '0' }), animate('{{ enterDuration }} ease-out', style({ opacity: '1' }))],
        { params: { enterDuration: '.4s' } }
      )
    ])
  ],
  host: {
    '(document:keydown)': 'onKeyDown($event)'
  }
})
export class OverlayComponent implements OnInit {
  private browserService = inject(BrowserService)
  element = inject(ElementRef)

  readonly showCloseButton = input(true)
  readonly isBehindModals = input(false)
  readonly solid = input(false)
  readonly color = input<OverlayColor>(Color.Ice)
  readonly blur = input(true)
  readonly hideOnEscape = input(false)
  readonly isTopLayer = input(false)
  readonly skipEnterAnimation = input(false)

  readonly showing = output<void>()
  readonly hiding = output<void>()

  private readonly _visible = signal(false)
  private readonly overlayRef = viewChild<ElementRef<HTMLElement>>('overlayRef')

  constructor() {
    effect(() => this.setPopoverStateOnVisible())
  }

  get visible() {
    return this._visible.asReadonly()
  }

  protected readonly Size = Size

  ngOnInit() {}

  /**
   * Synchronizes the native popover state with the overlay visibility when the
   * overlay is configured to render in the browser top layer.
   */
  private setPopoverStateOnVisible(): void {
    if (!this.isTopLayer()) {
      return
    }

    const overlayRef = this.overlayRef()?.nativeElement

    if (typeof overlayRef?.showPopover !== 'function') {
      return
    }

    if (this._visible() && !overlayRef.matches(':popover-open')) {
      overlayRef.showPopover()
    } else if (!this._visible() && overlayRef.matches(':popover-open')) {
      overlayRef.hidePopover()
    }
  }

  /**
   * Show the overlay component
   */
  show(): void {
    if (!this._visible()) {
      this._visible.set(true)
      this.showing.emit()
      this.browserService.setBodyScrolling(false)

      if (this.blur()) {
        this.browserService.blurMainContent(true, false)
      }
    }
  }

  /**
   * Hide the overlay component
   */
  hide(): void {
    if (this._visible()) {
      this._visible.set(false)
      this.browserService.setBodyScrolling(true)

      if (this.blur()) {
        this.browserService.blurMainContent(false, false)
      }

      this.hiding.emit()
    }
  }

  /**
   * Handles keydown events
   * @param {Event} event
   */
  onKeyDown(event: Event): void {
    const isKeyboardEvent = event instanceof KeyboardEvent

    if (!isKeyboardEvent) {
      return
    }

    switch (event.key) {
      case 'Escape':
        if (this.hideOnEscape()) {
          this.hide()
        }

        break
    }
  }
}
